import { useState, useEffect } from 'react'
import { ethers } from 'ethers'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import contractAbi from 'artifacts/contracts/Maups.sol/Maups.json'
import {
  requiredNetworkID,
  contractAddress,
  chainParameter,
  isOnWhitelist,
  generateProof,
  notify,
} from 'utils'
import { Account, Loading, ContractData } from 'types'
import { Info, Nav, About, Mint, Showcase, Footer } from 'components'

const { ethereum }: any = window

function App() {
  const [message, setMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<Loading>({
    nav: false,
    mint: false,
  })
  const [contractState, setContractState] = useState<any>()
  const [contractData, setContractData] = useState<ContractData>({
    isPaused: true,
    cost: '',
    maxSupply: '',
    totalSupply: '',
    balanceOf: '',
    walletOfOwner: [],
    revealed: false,
    whitelistMintEnabled: false,
    whitelistClaimed: false,
  })
  const [account, setAccount] = useState<Account>({
    address: '',
    balance: '',
    onWhitelist: false,
  })

  useEffect(() => {
    ;(async () => {
      /*
        ============================
        INITIAL CHECKS BEFORE RENDER
        ============================
      */
      if (!ethereum) {
        notify('error', 'Please Install MetaMask', 'metamask')
        setMessage('Install MetaMask')
        return
      }

      ethereum.on('chainChanged', () => window.location.reload())
      if (
        (await ethereum.request({ method: 'net_version' })) !==
        requiredNetworkID
      ) {
        notify(
          'error',
          `Please change Network to: ${chainParameter[requiredNetworkID].chainName}`,
          'chainid'
        )
        setMessage('Wrong Network')
        return
      }
      ethereum.on('accountsChanged', () => window.location.reload())

      /*
        =========================
        INITIALIZE SMART CONTRACT
        =========================
      */
      try {
        const provider = new ethers.providers.Web3Provider(ethereum)
        const contract = new ethers.Contract(
          contractAddress,
          contractAbi.abi,
          provider
        )

        setContractState(contract)
        const isPaused = await contract.paused()
        const cost = await contract.cost()
        const maxSupply = await contract.maxSupply()
        const revealed = await contract.revealed()
        const totalSupply = await contract.totalSupply()
        const whitelistMintEnabled = await contract.whitelistMintEnabled()

        setContractData(
          (prevState: ContractData): ContractData => ({
            ...prevState,
            isPaused,
            cost: ethers.utils.formatEther(cost),
            maxSupply: maxSupply.toString(),
            totalSupply: totalSupply.toString(),
            revealed,
            whitelistMintEnabled,
          })
        )
      } catch (error) {
        console.log('Failed initializing Contract ' + error)
      }

      /*
        ============================
        CEHCK IF WALLET IS CONNECTED
        ============================
      */
      try {
        const [acc] = await ethereum.request({ method: 'eth_accounts' })
        const balance = await ethereum.request({
          method: 'eth_getBalance',
          params: [acc, 'latest'],
        })

        if (acc) {
          setAccount(
            (prevState: Account): Account => ({
              ...prevState,
              address: acc,
              balance: ethers.utils.formatEther(balance),
            })
          )
        } else {
          console.log('Account not found!')
        }
      } catch (error) {
        console.log('MetaMask is not connected ' + error)
        notify('warn', 'Please Connect MetaMask.', 'walletacc')
      }

      /*
        ========
        CLEAN-UP
        ========
      */
      return () => {
        ethereum.removeListener('accountsChanged', () =>
          window.location.reload()
        )
        ethereum.removeListener('chainChanged', () => window.location.reload())
      }
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      /*
        =========================
        GET ACCOUNT CONTRACT DATA
        =========================
      */
      if (!!account.address) {
        try {
          const balanceOf = await contractState.balanceOf(account.address)
          const walletOfOwner = await contractState.walletOfOwner(
            account.address
          )
          const whitelistClaimed = await contractState.whitelistClaimed(
            account.address
          )

          if (!whitelistClaimed) {
            const res = await isOnWhitelist(account.address)
            setAccount(
              (prevState: Account): Account => ({
                ...prevState,
                onWhitelist: res.onWhitelist,
              })
            )
          }
          setContractData(
            (prevState: ContractData): ContractData => ({
              ...prevState,
              balanceOf: balanceOf.toString(),
              walletOfOwner,
              whitelistClaimed,
            })
          )
        } catch (error) {
          console.log('Account contract data: ' + error)
        }
      }
    })()
  }, [
    contractState,
    account.address,
    contractData.totalSupply,
    contractData.whitelistClaimed,
  ])

  /*
    ==============================
    CONTRACT INTERACTION FUNCTIONS
    ==============================
  */
  const connectWalletHandler = async (): Promise<void> => {
    try {
      setIsLoading(
        (prevState: Loading): Loading => ({
          ...prevState,
          nav: true,
        })
      )
      const [acc] = await ethereum.request({
        method: 'eth_requestAccounts',
      })
      const balance = await ethereum.request({
        method: 'eth_getBalance',
        params: [acc, 'latest'],
      })

      setAccount(
        (prevState: Account): Account => ({
          ...prevState,
          address: acc,
          balance: ethers.utils.formatEther(balance),
        })
      )
      setIsLoading(
        (prevState: Loading): Loading => ({
          ...prevState,
          nav: false,
        })
      )
    } catch (error) {
      console.log(error)
      setIsLoading(
        (prevState: Loading): Loading => ({
          ...prevState,
          nav: false,
        })
      )
    }
  }

  const changeNetworkHandler = async (): Promise<void> => {
    try {
      await ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            ...chainParameter[requiredNetworkID],
          },
        ],
      })
    } catch (error) {
      console.log(error)
    }
  }

  const mintHandler = async (): Promise<void> => {
    try {
      const provider = new ethers.providers.Web3Provider(ethereum)
      const signer = provider.getSigner()
      const contract = new ethers.Contract(
        contractAddress,
        contractAbi.abi,
        signer
      )
      setIsLoading(
        (prevState: Loading): Loading => ({
          ...prevState,
          mint: true,
        })
      )
      const tx = await contract.mint(ethers.BigNumber.from(1), {
        value: ethers.utils.parseEther(contractData.cost),
      })
      setIsLoading(
        (prevState: Loading): Loading => ({
          ...prevState,
          mint: false,
        })
      )
      notify('', `Transaction in process... 🙈`, 'mintprocess')
      await tx.wait()
      notify('success', `Successfully minted! 🐒`, 'mintminted')
      setContractData(
        (prevState: ContractData): ContractData => ({
          ...prevState,
          totalSupply: (+prevState.totalSupply + 1).toString(),
        })
      )
    } catch (e) {
      notify('warn', (e as Error).message, 'minterror')
      setIsLoading(
        (prevState: Loading): Loading => ({
          ...prevState,
          mint: false,
        })
      )
    }
  }

  const whitelistMintHandler = async (): Promise<void> => {
    try {
      const provider = new ethers.providers.Web3Provider(ethereum)
      const signer = provider.getSigner()
      const contract = new ethers.Contract(
        contractAddress,
        contractAbi.abi,
        signer
      )
      const merkleProof = await generateProof(account.address)

      setIsLoading(
        (prevState: Loading): Loading => ({
          ...prevState,
          mint: true,
        })
      )
      const tx = await contract.whitelistMint(
        ethers.BigNumber.from(1),
        merkleProof,
        {
          value: ethers.utils.parseEther('0'),
        }
      )
      setIsLoading(
        (prevState: Loading): Loading => ({
          ...prevState,
          mint: false,
        })
      )
      notify('', `Transaction in process... 🙈`, 'mintprocess')
      await tx.wait()
      notify('success', `Successfully minted! 🐒`, 'mintminted')
      setContractData(
        (prevState: ContractData): ContractData => ({
          ...prevState,
          totalSupply: (+prevState.totalSupply + 1).toString(),
          whitelistClaimed: true,
        })
      )
    } catch (e) {
      notify('warn', (e as Error).message, 'minterror')
      setIsLoading(
        (prevState: Loading): Loading => ({
          ...prevState,
          mint: false,
        })
      )
    }
  }

  return (
    <>
      <Info />
      <div className="app">
        <Nav
          message={message}
          isLoading={isLoading}
          account={account}
          connectWalletHandler={connectWalletHandler}
          changeNetworkHandler={changeNetworkHandler}
        />
        <About />
        <Mint
          message={message}
          account={account}
          contractData={contractData}
          mintHandler={mintHandler}
          isLoading={isLoading}
          whitelistMintHandler={whitelistMintHandler}
        />
        <Showcase account={account} contractData={contractData} />
        <Footer />
        <ToastContainer />
      </div>
    </>
  )
}

export default App
